import type { CommentNode } from "../../comments/explorer/types";
import { getCommentDisplayText } from "../../comments/explorer/commentText";

export type WorkspaceThreadFilter = "all" | "anchored" | "general";

export function filterRootThreadsByKind(
  roots: CommentNode[],
  anchoredRootCommentIds: Set<number>,
  filter: WorkspaceThreadFilter,
): CommentNode[] {
  if (filter === "all") return roots;
  if (filter === "anchored") {
    return roots.filter((root) => anchoredRootCommentIds.has(root.id));
  }
  return roots.filter((root) => !anchoredRootCommentIds.has(root.id));
}

function normalizeSearchText(value: unknown): string {
  return String(value || "").trim().toLowerCase();
}

function getCommentUserEmail(comment: CommentNode): string {
  const userWithOptionalEmail = comment.user as { email?: string | null } | undefined;
  return userWithOptionalEmail?.email ?? "";
}

function commentMatchesQuery(comment: CommentNode, query: string): boolean {
  if (!query) return true;

  const searchableText = [
    getCommentDisplayText(comment),
    comment.user?.name,
    getCommentUserEmail(comment),
    comment.isRemoved ? "removed" : "active",
  ]
    .map(normalizeSearchText)
    .filter(Boolean)
    .join(" ");

  return searchableText.includes(query);
}

export function threadMatchesQuery(root: CommentNode, rawQuery: string): boolean {
  const query = normalizeSearchText(rawQuery);
  if (!query) return true;

  const stack: CommentNode[] = [root];
  const seen = new Set<number>();

  while (stack.length) {
    const current = stack.pop();
    if (!current || seen.has(current.id)) continue;
    seen.add(current.id);

    if (commentMatchesQuery(current, query)) return true;
    current.replies.forEach((reply) => stack.push(reply));
  }

  return false;
}

export function filterRootThreadsForInbox(
  roots: CommentNode[],
  anchoredRootCommentIds: Set<number>,
  filter: WorkspaceThreadFilter,
  query: string,
): CommentNode[] {
  return filterRootThreadsByKind(roots, anchoredRootCommentIds, filter).filter((root) =>
    threadMatchesQuery(root, query),
  );
}
